import {
    AfterViewInit,
    Component, EventEmitter,
    Input,
    OnChanges,
    OnInit, Output,
    Renderer2,
    RendererFactory2,
    SecurityContext, SimpleChanges,
    Type,
    ViewChild
} from '@angular/core';
import {o, OdataQuery} from "odata";
import {FilterComponent} from "./filters/filter/filter.component";
import {DomSanitizer} from "@angular/platform-browser";
import * as deepEqual from "fast-deep-equal";
import {Subject} from "rxjs";

type User = {
    ADUsername: string;
};

export enum Selectability {
    None,
    SingleSelect,
    MultiSelect
}

export type CollectionPropertyFilter = {
    CollectionPropertyName: string,
    FilterPropertyName?: string,
}

export type TableConfig = {
    ODataResourceName: string;
    Selectability?: Selectability;
    Columns: ColumnConfig[];
    AnyFieldFilterPropertyNames?: (CollectionPropertyFilter|string)[];
};

export type ColumnConfig = {
    DisplayName: string;
    IconHeader?: {
      IconSet?: string,
      IconName: string
    },
    PropertyName: string;
    // Treat an absent 'EnableSorting' value as true
    EnableSorting?: boolean;
    // If present, overrides 'EnableSorting'
    SortProperty?: string;
    // If absent, defaults to PropertyName
    FilterProperty?: string;
    FilterType?: Type<FilterComponent>;
    FilterInputs?: any;
    IsDefaultFilter?: boolean;
    Formatter?: (val: any, row?: any) => string | HTMLElement;
    IsHidden?: boolean;
    [OtherProperties: string]: any;
};

export type OrderByExpression = {
    Property: string,
    Descending: boolean
};

@Component({
    selector: 'data-table',
    templateUrl: './data-table.component.html',
    styleUrls: ['./data-table.component.less']
})
export class DataTableComponent implements OnInit, OnChanges, AfterViewInit {
    //@Input('filter') public Filter: FilterComponent;
    @Input('config')
    public Config: TableConfig;

    @Input('OrderBy')
    public OrderByClause: OrderByExpression[] = [];
    
    public get VisibleColumns(): ColumnConfig[] {
        return this.Config.Columns.filter(c => !c.IsHidden);
    }
    
    public Rows: any[];
    
    private _rowCount: number;
    
    public set RowCount(value: number) {
        this._rowCount = value;
        let pageCount = (Math.floor((value - 1) / this.PageSize) + 1);
        pageCount = Math.max(pageCount, 1);
        this.PageCountChange.emit(pageCount);
    }
    
    public DefaultFormatter = (x: any) => (x == null ? '' : x).toString();
    
    private _filter: string;
    
    @Input('Filter')
    public set Filter(v: string) {
        this._filter = v;
        this.PageIndex = 0;
        this.RefreshInternal();
    }
    
    @Input('PageSize')
    public PageSize: number;
    
    private _pageIndex: number = 0;
    
    @Input('PageIndex')
    public set PageIndex(value: number) {
        if (value == null || value == this._pageIndex) {
            return;
        }
        this._pageIndex = value;
        this.PageIndexChange.emit(value);
        this.RefreshInternal();
    }
    
    public get PageIndex() {
        return this._pageIndex;
    }

    @Output('PageIndexChange')
    public PageIndexChange = new EventEmitter<number>(true);
    
    @Output('PageCountChange')
    public PageCountChange = new EventEmitter<number>(true);
    
    public SecurityContext = SecurityContext;
    
    @ViewChild('dataTable')
    private _table: HTMLTableElement;
    
    private _selectedIndices = new Set<number>();
    
    @Input('Ready')
    public ParentReady: boolean = true;
    
    private _ready: boolean = false;
    
    constructor() {
    }
    
    ngOnInit() {
        this.RefreshInternal();
    }
    
    ngOnChanges(changes: SimpleChanges) {
        // if (this._table) {
        //     let tooltipElements = this._table.querySelectorAll('[data-bs-toggle=tooltip]');
        //     tooltipElements.forEach((e) => new (<any>window).bootstrap.Tooltip(e));
        // }
    }
    
    ngAfterViewInit() {
        this._ready = true;
        this.RefreshInternal();
    }
    
    public GetHeaderSortIconClass(col: ColumnConfig) {
        let property = col.SortProperty || col.PropertyName;
        let orderByExpression = this.OrderByClause.find(expr => expr.Property == property);
        return {
            'fa-arrow-up':        orderByExpression && !orderByExpression.Descending,
            'fa-arrow-down':      orderByExpression &&  orderByExpression.Descending,
            'fa-arrows-up-down': !orderByExpression
        }
    }

    public OrderBy(propertyName: string) {
        if (this.OrderByClause.length == 1 && this.OrderByClause[0].Property == propertyName) {
            this.OrderByClause[0].Descending = !this.OrderByClause[0].Descending;
        }
        else
        {
            this.OrderByClause = [{
                Property: propertyName,
                Descending: false
            }];
        }
        this.PageIndex = 0;
        
        this.RefreshInternal();
    }
    
    private _lastQuery: OdataQuery;
    
    private IsReady() {
        return this.ParentReady && this._ready;
    }
    
    public Refresh() {
        this.RefreshInternal(true);
    }
    
    private RefreshInternal(force: boolean = false) {
        
        if (!this.IsReady()) {
            return;
        }
        
        let query: OdataQuery = {
            $count: true
        };
        
        if (this._filter) {
            query.$filter = this._filter;
        }
        
        if (this.OrderByClause.length) {
            query.$orderby = this.OrderByClause
                .map(expr => `${expr.Property} ${expr.Descending ? 'desc' : 'asc'}`)
                .join(',');
        }
        
        if (this.PageSize != null) {
            query.$skip = this.PageIndex * this.PageSize;
            query.$top = this.PageSize;
        }
        
        if (!force && deepEqual(query, this._lastQuery)) {
            return;
        }
        
        this._lastQuery = query;
        
        o('/', { credentials: "include" })
            .get('odata/' + this.Config.ODataResourceName)
            .fetch(query)
            .then(response => {
                return (<any>response).json();
            })
            .then(odata => {
                this.RowCount = odata['@odata.count'];
                this.Rows = odata.value;
            });
    }
    
    public ExtractProperty(row: any, propertyPath: string) {
        let propertyNames = propertyPath.split('/');
        let currentObject = row;
        for (let propertyName of propertyNames) {
            currentObject = currentObject[propertyName];
            if (currentObject == null) {
                break;
            }
        }
        
        return currentObject;
    }
    
    public Select(index: number) {
        if (this.Config.Selectability == null || this.Config.Selectability == Selectability.None) {
            return;
        }
        else if (this.Config.Selectability == Selectability.SingleSelect) {
            this._selectedIndices.clear();
        }
        this._selectedIndices.add(index);
    }
    
    public IsSelected(index: number) {
        return this._selectedIndices.has(index);
    }
    
    public GetSelection() {
        let index = this._selectedIndices.values().next().value;
        if (index !== undefined) {
            return this.Rows[index];
        }
    }
    
    public GetSelections() {
        return Array.from(this._selectedIndices).map(i => this.Rows[i]);
    }
}
